import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, Paperclip } from 'lucide-react';
import { Botao, Selo } from './base';
import { Modal } from './sobreposicao';
import { get, mensagemDeErro, post, textoDeErroGravado } from '../nucleo/api';
import { nomeDeArquivo, nomePessoa, textoLivre } from '../nucleo/formato';
import { AreaSensivel } from './formulario';

/**
 * Fase 2 (W6, L11): peças web do framework de importação do núcleo. O descritor mora no módulo dono (`r.importador`);
 * o web só precisa do `caminho` REST do recurso (`/cadastros/servicos`): o modelo, as colunas, a prévia e a importação
 * saem de `<caminho>/importar...`.
 */

export interface ColunaImportacaoWeb {
  nome: string;
  rotulo: string;
  tipo: 'texto' | 'numero' | 'dinheiro' | 'booleano' | 'data' | 'lista';
  obrigatoria: boolean;
  valores: string[] | null;
  extra: string | null;
  /** Fase 2 (W14): grupo da coluna (dados, endereço, conta bancária); os campos personalizados formam o grupo `extra`. */
  grupo?: string | null;
}

interface ErroLinha {
  linha: number;
  coluna?: string;
  codigo: string;
  motivo: string;
  /** Fase 2 (W14): registros parecidos para conferir (ex.: parceiro pelo nome quando o documento não casou). */
  sugestoes?: { id: string; nome: string; documento: string | null }[];
}

interface ResultadoImportacao {
  simulacao: boolean;
  total: number;
  criados: number;
  atualizados: number;
  existentes?: number;
  contagens?: Record<string, number>;
  erros: ErroLinha[];
  erros_total: number;
  /** Lote de 19/10: observações por linha que não recusam (gestor com várias pessoas, linha repetida pulada). */
  avisos?: ErroLinha[];
  avisos_total?: number;
  ignoradas: string[];
}

interface Job {
  estado: 'pendente' | 'rodando' | 'concluido' | 'erro';
  progresso: number;
  resultado: ResultadoImportacao | null;
  erro: string | null;
}

/**
 * Lote de 19/10: a coluna do CSV (pelo cabeçalho, sem acento e sem caixa, em qualquer posição) e quantas linhas a deixam vazia.
 * Leitura no navegador só para a tela decidir o que pedir (o servidor lê de novo e decide de verdade).
 */
export function colunaDoCsv(csv: string, nomes: readonly string[]): { presente: boolean; vazias: number } {
  const texto = csv.replace(/^\uFEFF/, '');
  const primeira = texto.split(/\r?\n/, 1)[0] ?? '';
  const sep = primeira.includes(';') && (primeira.match(/;/g)?.length ?? 0) >= (primeira.match(/,/g)?.length ?? 0) ? ';' : ',';
  const linhas: string[][] = [];
  let celulas: string[] = [];
  let campo = '';
  let aspas = false;
  const fechar = () => {
    celulas.push(campo);
    if (celulas.some((c) => c.trim()) && !(celulas[0] ?? '').trim().startsWith('#')) linhas.push(celulas);
    celulas = [];
    campo = '';
  };
  for (let i = 0; i < texto.length; i++) {
    const c = texto[i]!;
    if (aspas) {
      if (c === '"' && texto[i + 1] === '"') {
        campo += '"';
        i++;
      } else if (c === '"') aspas = false;
      else campo += c;
    } else if (c === '"') aspas = true;
    else if (c === sep) {
      celulas.push(campo);
      campo = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && texto[i + 1] === '\n') i++;
      fechar();
    } else campo += c;
  }
  if (campo !== '' || celulas.length) fechar();
  const norma = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const alvo = new Set(nomes.map(norma));
  const [cab, ...dados] = linhas;
  const idx = (cab ?? []).findIndex((h) => alvo.has(norma(h)));
  if (idx < 0) return { presente: false, vazias: dados.length };
  return { presente: true, vazias: dados.filter((l) => !(l[idx] ?? '').trim()).length };
}

/** Endereço do modelo CSV no idioma do app (a linha de dicas sai traduzida). */
export const urlModeloCsv = (caminho: string, idioma: string) => `/api/v1${caminho}/importar/modelo?idioma=${['pt', 'en', 'es'].includes(idioma) ? idioma : 'pt'}`;

/** "Baixar modelo", ao lado de "Importar": baixa o CSV gerado do descritor (colunas reais, inclusive campos personalizados). */
export function BotaoModeloCsv({ caminho }: { caminho: string }) {
  const { t, i18n } = useTranslation();
  return (
    <a
      href={urlModeloCsv(caminho, i18n.language)}
      download
      data-sem-impressao=""
      data-modelo-csv=""
      title={t('importacao.modeloTitulo')}
      className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-borda bg-sup-2 px-4 text-sm font-medium text-tinta-1 transition-colors hover:bg-sup-3"
    >
      <Download className="size-4" aria-hidden />
      {t('importacao.modelo')}
    </a>
  );
}

/** Coluna aceita: nome técnico em mono, tipo, valores da lista e obrigatoriedade. */
function ItemColuna({ c }: { c: ColunaImportacaoWeb }) {
  const { t } = useTranslation();
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 py-1 text-xs">
      <code className="numero font-medium text-tinta-1">{c.nome}</code>
      {c.extra && <span className="min-w-0 break-words text-tinta-3">{c.rotulo}</span>}
      <span className="text-tinta-3">{t(`importacao.tipos.${c.tipo}`)}</span>
      {c.valores?.length ? <span className="min-w-0 break-words text-tinta-3">({c.valores.join(', ')})</span> : null}
      <Selo cor={c.obrigatoria ? 'laranja' : 'neutro'}>{c.obrigatoria ? t('importacao.obrigatoria') : t('importacao.opcional')}</Selo>
    </li>
  );
}

/** Grupo da coluna: o declarado pelo importador, ou `extra` para campo personalizado. */
const grupoDe = (c: ColunaImportacaoWeb) => c.grupo ?? (c.extra ? 'extra' : null);

/** Colunas aceitas; com grupo declarado (fase 2, W14: pessoas), uma lista por grupo com o nome do grupo. */
function ColunasAgrupadas({ colunas }: { colunas: ColunaImportacaoWeb[] }) {
  const { t } = useTranslation();
  if (!colunas.some((c) => c.grupo)) {
    return (
      <ul className="mt-1 divide-y divide-borda">
        {colunas.map((c) => (
          <ItemColuna key={c.nome} c={c} />
        ))}
      </ul>
    );
  }
  const grupos = [...new Set(colunas.map(grupoDe))];
  return (
    <div className="mt-2 flex flex-col gap-3" data-colunas-agrupadas="">
      {grupos.map((g) => (
        <div key={g ?? 'sem'}>
          {g && <p className="kicker mb-1">{t(`importacao.grupos.${g}`, { defaultValue: g })}</p>}
          <ul className="divide-y divide-borda">
            {colunas
              .filter((c) => grupoDe(c) === g)
              .map((c) => (
                <ItemColuna key={c.nome} c={c} />
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Resumo de um job de importação (prévia ou gravação) com os erros por linha. */

function ResumoImportacao({ r }: { r: ResultadoImportacao }) {
  const { t } = useTranslation();
  const valores = { criados: r.criados, atualizados: r.atualizados, erros: r.erros_total };
  const naoListados = Math.max(0, r.erros_total - new Set(r.erros.map((e) => e.linha)).size);
  const avisosNaoListados = Math.max(0, (r.avisos_total ?? 0) - new Set((r.avisos ?? []).map((e) => e.linha)).size);
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{t(r.simulacao ? 'importacao.previa' : 'importacao.resultado', valores)}</p>
      {!!r.existentes && <p className="text-xs text-tinta-3">{t('importacao.existentes', { count: r.existentes })}</p>}
      {Object.entries(r.contagens ?? {})
        .filter(([, n]) => n > 0)
        .map(([k, n]) => (
          <p key={k} className="text-xs text-tinta-3">
            {t(`importacao.contagens.${k}`, { count: n, defaultValue: `${k}: ${n}` })}
          </p>
        ))}
      {r.simulacao && r.erros_total > 0 && <p className="text-xs text-tinta-3">{t('importacao.previaDica')}</p>}
      {r.ignoradas.length > 0 && <p className="text-xs text-tinta-3">{t('importacao.ignoradas', { lista: r.ignoradas.join(', ') })}</p>}
      {r.erros.length > 0 && (
        <div>
          <p className="text-xs font-medium text-tinta-2">{t('importacao.linhasComErro')}</p>
          <ul tabIndex={0} role="region" aria-label={t('importacao.linhasComErro')} className="mt-1 max-h-40 overflow-y-auto text-xs text-tinta-3">
            {r.erros.map((e, i) => (
              <li key={`${e.linha}-${i}`} className="py-0.5">
                <span className="numero">{t('importacao.linha', { linha: e.linha })}</span>
                {e.coluna && <code className="numero ml-2 text-tinta-2">{e.coluna}</code>} {textoLivre(textoDeErroGravado(`${e.codigo}: ${e.motivo}`))}
                {e.sugestoes?.length ? <span className="block pl-4">{t('importacao.sugestoes', { lista: e.sugestoes.map((s) => nomePessoa(s.nome)).join(', ') })}</span> : null}
              </li>
            ))}
          </ul>
          {naoListados > 0 && <p className="mt-1 text-xs text-tinta-3">{t('importacao.maisErros', { count: naoListados })}</p>}
        </div>
      )}
      {!!r.avisos?.length && (
        <div>
          <p className="text-xs font-medium text-tinta-2">{t('importacao.linhasComAviso')}</p>
          <ul tabIndex={0} role="region" aria-label={t('importacao.linhasComAviso')} className="mt-1 max-h-40 overflow-y-auto text-xs text-tinta-3">
            {r.avisos.map((e, i) => (
              <li key={`${e.linha}-${i}`} className="py-0.5">
                <span className="numero">{t('importacao.linha', { linha: e.linha })}</span>
                {e.coluna && <code className="numero ml-2 text-tinta-2">{e.coluna}</code>} {textoLivre(textoDeErroGravado(`${e.codigo}: ${e.motivo}`))}
              </li>
            ))}
          </ul>
          {avisosNaoListados > 0 && <p className="mt-1 text-xs text-tinta-3">{t('importacao.maisAvisos', { count: avisosNaoListados })}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * Diálogo de importação do núcleo: lista as colunas aceitas (em vez de um exemplo fixo), lê o CSV colado ou o arquivo,
 * SIMULA primeiro (prévia, nada é gravado) e só então importa. O resultado traz os erros por linha.
 */
export function DialogoImportacao({
  aberto,
  aoFechar,
  caminho,
  titulo,
  aoConcluir,
  csvInicial,
  aviso,
  complemento,
  cabecalhos,
}: {
  aberto: boolean;
  aoFechar: () => void;
  caminho: string;
  titulo: string;
  aoConcluir?: () => void;
  /** Trilha Conta Azul: o arquivo já gerado (o conversor do espelho), no lugar de escolher um; a prévia e a gravação são as mesmas. */
  csvInicial?: { csv: string; nome: string } | null;
  /** Lote de 19/10: observação do importador acima do texto padrão (Funcionários: "Ninguém recebe convite..."). */
  aviso?: string;
  /**
   * Lote de 19/10: escolha da tela que vale para o arquivo inteiro (Funcionários: a empresa das linhas sem empresa). `opcoes` vai
   * na prévia e na gravação; trocar a escolha pede nova prévia; `bloqueio` impede gravar (a prévia continua, e avisa).
   */
  complemento?: (csv: string) => { conteudo: ReactNode; opcoes?: Record<string, string>; bloqueio?: string | null };
  /** Rodada rval1-1 (RVAL1-ROB-M2): `X-Empresa` da empresa escolhida na tela (troca de empresa desligada), na prévia e na gravação. */
  cabecalhos?: Record<string, string>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [simulado, setSimulado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [modo, setModo] = useState<'simular' | 'gravar'>('simular');
  const seletor = useRef<HTMLInputElement>(null);
  const comp = complemento?.(csv);
  const assinatura = `${csv}\u0000${JSON.stringify(comp?.opcoes ?? {})}`;
  const enviado = useRef('');
  useEffect(() => {
    if (!aberto || !csvInicial) return;
    setCsv(csvInicial.csv);
    setNomeArquivo(csvInicial.nome);
    setJobId(null);
    setSimulado(null);
    setErro(null);
  }, [aberto, csvInicial]);
  const colunas = useQuery({
    queryKey: [caminho, 'importar', 'colunas'],
    queryFn: () => get<{ colunas: ColunaImportacaoWeb[]; chave_atualizacao: string[] }>(`${caminho}/importar/colunas`),
    enabled: aberto,
  });
  const job = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => get<Job>(`/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && ['concluido', 'erro'].includes(q.state.data.estado) ? false : 1000),
  });
  const pronto = job.data?.estado === 'concluido' ? job.data.resultado : null;
  const gravou = !!pronto && !pronto.simulacao;
  useEffect(() => {
    if (pronto?.simulacao) setSimulado(enviado.current);
    if (gravou) {
      void qc.invalidateQueries({ queryKey: [caminho] });
      aoConcluir?.();
    }
    // Só reage ao fim do job; o conteúdo simulado é o do momento do envio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronto]);
  const fechar = () => {
    setCsv('');
    setNomeArquivo('');
    setJobId(null);
    setSimulado(null);
    setErro(null);
    aoFechar();
  };
  const enviar = async (simular: boolean) => {
    setErro(null);
    setModo(simular ? 'simular' : 'gravar');
    setEnviando(true);
    enviado.current = assinatura;
    try {
      setJobId((await post<{ job_id: string }>(`${caminho}/importar`, { csv, simular, ...(comp?.opcoes && Object.keys(comp.opcoes).length ? { opcoes: comp.opcoes } : {}) }, cabecalhos)).job_id);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setEnviando(false);
    }
  };
  const rodando = !!jobId && !pronto && job.data?.estado !== 'erro';
  const podeGravar = !!simulado && simulado === assinatura && !gravou && !rodando && !comp?.bloqueio;
  const chave = colunas.data?.chave_atualizacao ?? [];
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={titulo}
      largura="lg"
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {gravou ? t('comum.fechar') : t('comum.cancelar')}
          </Botao>
          {!gravou && (
            <Botao disabled={!csv.trim()} carregando={(enviando || rodando) && !podeGravar} onClick={() => void enviar(true)}>
              {simulado ? t('importacao.simularDeNovo') : t('importacao.simular')}
            </Botao>
          )}
          {!gravou && (
            <Botao variante="primario" disabled={!podeGravar} carregando={enviando && podeGravar} onClick={() => void enviar(false)}>
              {t('importacao.gravar')}
            </Botao>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {aviso && <p className="text-sm font-medium text-tinta-1">{aviso}</p>}
        <p className="text-sm text-tinta-2">{t('importacao.texto')}</p>
        {chave.length > 0 && <p className="text-sm text-tinta-2">{t('importacao.chave', { colunas: chave.join(', ') })}</p>}
        <div className="flex flex-wrap items-center gap-2">
          <BotaoModeloCsv caminho={caminho} />
        </div>
        <details className="rounded-lg border border-borda px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium">{t('importacao.colunas')}</summary>
          {colunas.isPending ? (
            <p className="py-1 text-xs text-tinta-3">{t('importacao.carregandoColunas')}</p>
          ) : colunas.isError ? (
            <p className="py-1 text-xs text-perigo">{mensagemDeErro(colunas.error)}</p>
          ) : (
            <ColunasAgrupadas colunas={colunas.data.colunas} />
          )}
        </details>
        {erro && <p className="text-sm text-perigo">{erro}</p>}
        {!gravou && (
          <>
            {/* Review r16 spec M3: o CSV colado é dado de cadastro (nome, CPF, e-mail, telefone, valor): no modo discreto, sem
                foco, vira o marcador (`mascara="oculto"`), como todo campo sensível; com foco, o texto real para editar. */}
            <AreaSensivel
              mascara="oculto"
              aria-label={t('importacao.conteudo')}
              rows={8}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              className="numero w-full rounded-lg border border-borda-campo bg-campo p-3 text-xs placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
            />
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <span className="text-sm text-tinta-2">{t('importacao.arquivo')}</span>
              <Botao icone={<Paperclip className="size-4" />} onClick={() => seletor.current?.click()}>
                {nomeArquivo ? t('importacao.trocarArquivo') : t('importacao.escolherArquivo')}
              </Botao>
              {nomeArquivo && (
                <span className="min-w-0 truncate text-sm text-tinta-2" title={nomeDeArquivo(nomeArquivo)}>
                  {nomeDeArquivo(nomeArquivo)}
                </span>
              )}
              <input
                ref={seletor}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                tabIndex={-1}
                aria-label={t('importacao.arquivo')}
                title={t('importacao.arquivo')}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  setNomeArquivo(f?.name ?? '');
                  setCsv((await f?.text()) ?? '');
                }}
              />
            </div>
          </>
        )}
        {!gravou && comp?.conteudo}
        {!gravou && comp?.bloqueio && <p className="text-sm text-laranja">{comp.bloqueio}</p>}
        {jobId && (
          <div role="status" className="rounded-lg border border-borda p-4">
            {rodando && (
              <>
                <p className="text-sm">{t(job.data?.estado !== 'rodando' ? 'importacao.naFila' : modo === 'simular' ? 'importacao.simulando' : 'importacao.importando')}</p>
                <div className="mt-2 h-2 w-full rounded-full bg-sup-3">
                  <div className={`h-2 rounded-full bg-acento-forte transition-all ${job.data?.estado !== 'rodando' ? 'animate-pulse' : ''}`} style={{ width: `${job.data?.estado === 'rodando' ? Math.max(5, job.data.progresso) : 5}%` }} />
                </div>
              </>
            )}
            {job.data?.estado === 'erro' && <p className="text-sm text-perigo">{textoDeErroGravado(job.data.erro)}</p>}
            {pronto && <ResumoImportacao r={pronto} />}
          </div>
        )}
      </div>
    </Modal>
  );
}
